<template>
    <div>
        <table class="table table-striped customer-highlight-table">
            <thead>
                <tr>
                    <th
                        v-for="(header, idx) in headers"
                        :key="idx"
                        @click="sortBy(idx, header.type)"
                        class="dashboard-sort-header"
                    >
                        <span v-if="sortColumn == idx">
                            <font-awesome-icon
                                v-if="sortDirection != 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection != 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ header.label }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="note in paginatedData"
                    :key="note.id"
                    :class="rowClass(note)"
                >
                    <td>{{ note.author__name }}</td>
                    <td>{{ formatDateToUS(note.time_created) }}</td>
                    <td>
                        <PartNumberDisplay
                            :part-number="note.part_number"
                            :selected-part="part_selected"
                        />
                    </td>
                    <td>{{ note.note }}</td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1"
                >
                    Previous
                </button>

                <span class="mx-3">
                    Page <strong>{{ currentPage }}</strong> of <strong>{{ totalPages }}</strong>
                </span>

                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatDateToUS } from '@/helpers/dates.js'
    import { normalizePart } from '@/helpers/string.js'
    import { buildDashboardRowHighlightClass } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        name: 'NotesTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            data: {
                type: Array,
                default: () => [],
            },
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
        },
        data() {
            return {
                headers: [
                    { label: 'Author', type: 'string' },
                    { label: 'Date', type: 'string' },
                    { label: 'Part Number', type: 'string' },
                    { label: 'Note', type: 'string' },
                ],
                columnType: 'string',
                sortColumn: 0,
                sortDirection: 'asc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal']),
            sortedData() {
                return this.data.slice().sort((a, b) => {
                    const key = this.getColumnKey(this.sortColumn)
                    let valueA = a[key]
                    let valueB = b[key]

                    if (this.columnType === 'numeric') {
                        valueA = Number(String(valueA).replace('%', '')) || 0
                        valueB = Number(String(valueB).replace('%', '')) || 0
                    } else if (this.columnType === 'date') {
                        valueA = new Date(valueA)
                        valueB = new Date(valueB)
                    } else {
                        valueA = String(valueA).toLowerCase()
                        valueB = String(valueB).toLowerCase()
                    }

                    return this.sortDirection === 'asc'
                        ? valueA > valueB
                            ? 1
                            : -1
                        : valueA < valueB
                          ? 1
                          : -1
                })
            },
            paginatedData() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedData.slice(start, end)
            },
            totalPages() {
                return Math.ceil(this.data.length / this.itemsPerPage)
            },
        },
        methods: {
            formatDateToUS,
            sortBy(columnIndex, columnType) {
                this.columnType = columnType

                if (this.sortColumn === columnIndex) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortColumn = columnIndex
                    this.sortDirection = 'asc'
                }
            },
            getColumnKey(index) {
                const keys = ['author__name', 'time_created', 'part_number', 'note']
                return keys[index]
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(note) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    selectedPart: this.part_selected,
                    partNumber: note?.part_number,
                    activePart: this.isActivePart(note.part_number),
                })
            },
        },
    }
</script>

<style scoped>
    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }
</style>
